import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Form, Button, Table, Badge, Alert } from 'react-bootstrap';
import API from '../services/api';
import SearchSelect from './SearchSelect';
import { formatDateTime } from '../utils/dates';

const dateTime = (d) => formatDateTime(d, 'Nunca');

// Admin-only (Clientes → "CG Connect"): who from this client can sign in to the CG Connect app, and — for
// distributors — which Manifold Manager client they are, so the app's "Mis Tanques" shows their cylinders.
const ClientPortalModal = ({ client, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [email, setEmail] = useState(client.email || '');
  const [name, setName] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [issued, setIssued] = useState(null); // { email, password, emailed, emailNote }
  const [manifoldNames, setManifoldNames] = useState(null);
  const [manifoldNote, setManifoldNote] = useState('');
  const [manifoldName, setManifoldName] = useState('');

  const load = useCallback(() => API.get(`/portal-access/clients/${client._id}`)
    .then(({ data }) => { setData(data); setManifoldName(data.client.manifoldClientName || ''); })
    .catch(err => setError(err.response?.data?.message || 'No se pudo cargar el acceso a CG Connect.')), [client._id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    API.get('/portal-access/manifold-clients')
      .then(({ data }) => { setManifoldNames(data.clients || []); if (data.unavailable) setManifoldNote(data.unavailable); })
      .catch(() => { setManifoldNames([]); setManifoldNote('No se pudo cargar la lista de Manifold Manager.'); });
  }, []);

  const run = async (key, fn) => {
    setBusy(key); setError('');
    try { const res = await fn(); await load(); onChanged?.(); return res; }
    catch (err) { setError(err.response?.data?.message || 'La acción falló.'); return null; }
    finally { setBusy(''); }
  };

  const showPassword = (res) => res && setIssued({ email: res.data.user.email, password: res.data.temporaryPassword, emailed: res.data.emailed, emailNote: res.data.emailNote });

  const create = async (e) => {
    e.preventDefault();
    const res = await run('create', () => API.post(`/portal-access/clients/${client._id}/users`, { email, name, sendEmail }));
    if (res) { showPassword(res); setEmail(''); setName(''); }
  };
  const reset = async (u) => {
    if (!window.confirm(`¿Generar una nueva contraseña temporal para ${u.email}? Su sesión actual en la app se cerrará.`)) return;
    showPassword(await run(`reset-${u._id}`, () => API.post(`/portal-access/users/${u._id}/reset-password`, { sendEmail })));
  };
  const toggle = (u) => run(`toggle-${u._id}`, () => API.patch(`/portal-access/users/${u._id}`, { active: !u.active }));
  const remove = (u) => window.confirm(`¿Eliminar el acceso de ${u.email}?`) && run(`del-${u._id}`, () => API.delete(`/portal-access/users/${u._id}`));
  const saveManifold = (value) => run('manifold', () => API.put(`/portal-access/clients/${client._id}/manifold`, { manifoldClientName: value }));

  const copy = (text) => navigator.clipboard?.writeText(text);
  const linked = data?.client?.manifoldClientName;

  return (
    <Modal show onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>CG Connect — {client.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="small text-muted">
          Con CG Connect el cliente ve en su teléfono su balance, sus facturas y sus pagos, hace pedidos y sigue sus entregas.
          Solo ve lo de este cliente.
        </p>
        {error && <Alert variant="danger">{error}</Alert>}

        {issued && (
          <Alert variant="success" onClose={() => setIssued(null)} dismissible>
            <div className="mb-1">Contraseña temporal de <strong>{issued.email}</strong> (se muestra solo esta vez):</div>
            <div className="d-flex align-items-center gap-2 mb-2">
              <code style={{ fontSize: '1.2rem' }}>{issued.password}</code>
              <Button size="sm" variant="outline-secondary" onClick={() => copy(issued.password)}><i className="bi bi-clipboard" /> Copiar</Button>
              <Button size="sm" variant="outline-secondary" onClick={() => copy(`Tu acceso a CG Connect (Caribbean Gas):\nCorreo: ${issued.email}\nContraseña temporal: ${issued.password}\nAl entrar, la app te pedirá elegir tu propia contraseña.`)}>
                <i className="bi bi-chat-text" /> Copiar mensaje
              </Button>
            </div>
            <div className="small">{issued.emailed ? `También se envió por correo a ${issued.email}.` : issued.emailNote || 'No se envió por correo: entrégala tú.'} Al entrar, la app le pedirá elegir su propia contraseña.</div>
          </Alert>
        )}

        <h6 className="mt-2">Personas con acceso</h6>
        {data === null ? <div className="text-muted small">Cargando...</div> : data.users.length === 0 ? (
          <div className="text-muted small mb-3">Nadie de este cliente tiene acceso todavía.</div>
        ) : (
          <Table size="sm" responsive className="align-middle">
            <thead><tr><th>Correo</th><th>Último acceso</th><th>Estado</th><th /></tr></thead>
            <tbody>
              {data.users.map(u => (
                <tr key={u._id}>
                  <td>{u.email}{u.name && <div className="small text-muted">{u.name}</div>}</td>
                  <td className="small">{dateTime(u.lastLoginAt)}</td>
                  <td>
                    <Badge bg={u.active ? 'success' : 'secondary'}>{u.active ? 'Activo' : 'Desactivado'}</Badge>
                    {u.active && u.mustChangePassword && <div className="small text-muted">Contraseña temporal</div>}
                    {u.pushDevices > 0 && <div className="small text-muted"><i className="bi bi-bell" /> Push activo</div>}
                  </td>
                  <td className="text-end">
                    <div className="d-flex gap-1 justify-content-end">
                      <Button size="sm" variant="outline-secondary" title="Nueva contraseña temporal" disabled={!!busy} onClick={() => reset(u)}><i className="bi bi-key" /></Button>
                      <Button size="sm" variant="outline-secondary" title={u.active ? 'Desactivar' : 'Activar'} disabled={!!busy} onClick={() => toggle(u)}>
                        <i className={`bi ${u.active ? 'bi-pause-circle' : 'bi-play-circle'}`} />
                      </Button>
                      <Button size="sm" variant="outline-danger" title="Eliminar acceso" disabled={!!busy} onClick={() => remove(u)}><i className="bi bi-trash" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        <Form onSubmit={create} className="p-3 mb-4" style={{ border: '1px solid var(--mm-line)', borderRadius: 8 }}>
          <div style={{ fontWeight: 600 }} className="mb-2">Dar acceso</div>
          <div className="d-flex flex-wrap gap-2 align-items-end">
            <div style={{ flex: '2 1 220px' }}>
              <Form.Label className="small text-muted mb-1">Correo (será su usuario)</Form.Label>
              <Form.Control type="email" required maxLength={254} value={email} onChange={e => setEmail(e.target.value)} placeholder="compras@cliente.com" />
            </div>
            <div style={{ flex: '1 1 160px' }}>
              <Form.Label className="small text-muted mb-1">Nombre <span className="text-muted">(opcional)</span></Form.Label>
              <Form.Control maxLength={80} value={name} onChange={e => setName(e.target.value)} />
            </div>
            <Button type="submit" disabled={busy === 'create'}>{busy === 'create' ? 'Creando...' : 'Crear acceso'}</Button>
          </div>
          <Form.Check className="mt-2" id="portal-send-email" label="Enviarle la contraseña temporal por correo" checked={sendEmail}
            onChange={e => setSendEmail(e.target.checked)} disabled={data && !data.mailConfigured} />
          {data && !data.mailConfigured && <div className="small text-muted">El correo no está configurado en este servidor: copia la contraseña y entrégala tú.</div>}
        </Form>

        <h6>Mis Tanques (distribuidores)</h6>
        <p className="small text-muted mb-2">
          Si este cliente trae sus propios cilindros a llenar, elige cómo se llama en Manifold Manager: la app le mostrará sus tanques en planta, llenados y despachados, en vivo.
        </p>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <div style={{ flex: '1 1 280px', maxWidth: 420 }}>
            {manifoldNames && manifoldNames.length > 0 ? (
              <SearchSelect options={manifoldNames} value={manifoldName} getKey={n => n} getLabel={n => n}
                onSelect={n => setManifoldName(n)} placeholder="Buscar cliente en Manifold Manager..." emptyLabel="Ningún cliente coincide" />
            ) : (
              <Form.Control value={manifoldName} maxLength={120} onChange={e => setManifoldName(e.target.value)} placeholder="Nombre exacto en Manifold Manager" />
            )}
          </div>
          <Button variant="outline-primary" disabled={busy === 'manifold' || !manifoldName.trim() || manifoldName === linked} onClick={() => saveManifold(manifoldName)}>Vincular</Button>
          {linked && <Button variant="outline-secondary" disabled={busy === 'manifold'} onClick={() => saveManifold(null)}>Quitar vínculo</Button>}
        </div>
        <div className="small mt-2">
          {linked ? <>Vinculado con <strong>{linked}</strong>.</> : <span className="text-muted">Sin vincular: la app no muestra la pestaña de tanques.</span>}
          {manifoldNote && <div className="text-muted">{manifoldNote}</div>}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>Cerrar</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ClientPortalModal;
